<script setup lang="ts">
import {computed, defineAsyncComponent, onMounted, ref} from 'vue'
import {getTranslation, getSettings, goTo, readMore, determineUspBoxWidth, getShopStatus} from '@/ts/utilities.ts'
import {GA4EcommerceItem, trackToDatalayer} from '@/ts/Helpers/tracking-helper.ts'
import {addToCart} from '@/ts/Helpers/cart-helper.ts'
import Cylindo from '@/ts/CustomBindings/Cylindo.ts'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'

const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const SelectElement = defineAsyncComponent(() => import('@/components/ui/form/SelectElement.vue'))
const Spinner = defineAsyncComponent(() => import('@/components/ui/decoration/Spinner.vue'))
const PriceElement = defineAsyncComponent(() => import('@/components/PriceElement.vue'))
const ShowModal = defineAsyncComponent(() => import('@/components/ShowModal.vue'))
const ProductCarousel = defineAsyncComponent(() => import('@/components/ProductCarousel.vue'))
const ProductGallery = defineAsyncComponent(() => import('@/components/ProductGallery.vue'))
const PowerStep = defineAsyncComponent(() => import('@/components/PowerStep.vue'))
const VariantSelector = defineAsyncComponent(() => import('@/components/VariantSelector.vue'))
const VariantModal = defineAsyncComponent(() => import('@/components/VariantModal.vue'))
const RadioGroup = defineAsyncComponent(() => import('@/components/ui/form/RadioGroup.vue'))
const Recommendations = defineAsyncComponent(() => import('@/components/Recommendations.vue'))
const PaymentMethodLogos = defineAsyncComponent(() => import('@/components/blocks/PaymentMethodLogos.vue'))
const VideoModal = defineAsyncComponent(() => import('@/components/VideoModal.vue'))
const VideoGallery = defineAsyncComponent(() => import('@/components/VideoGallery.vue'))
const SparXpres = defineAsyncComponent(() => import('@/components/SparXpres.vue'))
const AnyDayPrice = defineAsyncComponent(() => import('@/components/AnyDayPrice.vue'))
const USP = defineAsyncComponent(() => import("@/components/ui/USP.vue"))

import {AddToCartData, CarouselData, ProductViewData, RelewiseData} from '@/interfaces/ProductViewInterface'

interface Props {
	productId: string,
	variantId: string,
	relewiseData: object,
	areaId: number
}

const props = defineProps<Props>()

let product = ref(<ProductViewData>{})
const settings = getSettings()
let usps = ref({})
const showVideoModal = ref(false)

let productBoxes = ref([])
let productBullets = ref([])

const areaSelectedVariantColorGroupIds = ref([])

const relewiseData: RelewiseData = {
	isAnonymous: props.relewiseData.userIsAnonymous,
	showRecommendations: props.relewiseData.showRecommendations
}

let translations = ref({})
translations.value = {
	ProductNumber: getTranslation('ProductProductNumber'),
	Shortcuts: getTranslation('ShortCuts'),
	ProductDesignYourselfTitle: getTranslation('Product.DesignYourself.Title'),
	ProductInspirationBrochureTitle: getTranslation('Product.InspirationBrochure.Title'),
	ProductProductDescriptionAboutLinkText: getTranslation('Product.ProductDescription.AboutLinkText'),
	ProductProductDescriptionTitle: getTranslation('Product.ProductDescription.Title'),
	ProductProductDescriptionReadMoreTitle: getTranslation('Product.ProductDescription.ReadMore.Title'),
	ProductSpecificationsTitle: getTranslation('Product.Specifications.Title'),
	ProductSpecificationsReadMoreTitle: getTranslation('Product.Specifications.ReadMore.Title'),
	ProductViewInStore: getTranslation('Se produktet i en af vores butikker'),
	ProductAddToCart: getTranslation('ProductAddToCart'),
	HeaderNavFindShop: getTranslation('HeaderNav.FindShop'),
	ProductFinancingTitle: getTranslation('kr./måned i 10 måneder'),
	PreOrderButton: getTranslation('PreOrderButton'),
	NotForSaleMessage: getTranslation('Ecom:Product.NotForSaleMessage')
}

let showVariantModal = ref(false)
let showVariantSelector = ref(false)
let showMoreOptionsSelector = ref(false)
let selectedVariantId = ref('')
let selectedCylindoFeature = ref('')
let variantGroupData = ref({})

const selectedOptionalProducts: object = ref({})
const selectedVariantData: object = ref({})
const variantChange = (variantId, variant) => {
	if (variantId && variant) {
		setVariantId(variantId)
		showVariantModal.value = false
		showVariantSelector.value = false

		if (!product.value.cylindo.isUsingCylindoAssets) return
		setCylindoFeatures(variant)
	} else {
		console.error('MISSING VariantId / Variant')
	}
}

const variantInlineChange = (variant, group) => {
	const groupsIndex = product.value.variantOptionsAdvanced.variantGroups.findIndex(g => g.id === group.id)
	const variantIdParts = product.value.variantId.split('.')
	variantIdParts[groupsIndex] = variant.id
	setVariantId(variantIdParts.join('.'))

	// We want to display the selected variant, so we scroll to the top on mobile
	if (window.innerWidth < 768) {
		window.scrollTo(0, 0)
	}

	if (!product.value.cylindo.isUsingCylindoAssets) return
	setCylindoFeatures(variant)
}

const removeOptionalProduct = async (moreOptions: object) => {
	const removedProduct = {...selectedOptionalProducts.value[moreOptions.id]}
	delete selectedOptionalProducts.value[moreOptions.id]
	showMoreOptionsSelector.value = false

	let productId = props.productId
	if (selectedVariantId.value) {
		productId = `${props.productId}_${selectedVariantId.value}`
	}

	await getProductData(productId)

	if (!removedProduct?.product?.isCylindoProduct) return

	//remove value of feature so the key can be removed
	removedProduct.product.features = removedProduct.product.features.split(' ')[0]

	setCylindoFeatures(removedProduct.product)
}

const addOptionalProduct = async (product: object, moreOptions: object) => {
	selectedOptionalProducts.value[moreOptions.id] = {product: product, group: moreOptions}
	showMoreOptionsSelector.value = false

	let productId = props.productId
	if (selectedVariantId.value) {
		productId = `${props.productId}_${selectedVariantId.value}`
	}
	await getProductData(productId)

	if (!product.isCylindoProduct) return
	setCylindoFeatures(product)
}


const setVariantId = (variantId: object) => {
	selectedVariantId.value = variantId
	getProductData(`${props.productId}_${selectedVariantId.value}`)
}

const carouselData: CarouselData = ref({})

const setCarouselData = (product: ProductData) => {
	carouselData.value = {
		name: product.value.name,
		media: {
			images: product?.value.media?.images,
			moodImages: product?.value.media?.moodImages,
			videos: product?.value.media?.videos,
			productImage: product?.value.media?.productImage,
			productImageAltText: product?.value.media?.productImageAltText,
			// TODO: Refactor the next line to have correct data
			cylindoImages: [...Cylindo.getDetailsGrid(product?.value.cylindo.defaultFeatures.split(' '), product?.value.cylindo.id)],

		},
		cylindo: product.value.cylindo,
		productSplash: product.value.priceAndCampaign?.productSplash
	}
}

const setCylindoFeatures = (variant: object) => {
	if (variant.materialType) {
		Object.assign(Cylindo.selectedCylindoFeatures, {MATERIAL: variant.materialType})
		selectedCylindoFeature.value = `MATERIAL ${variant.materialType}`
	}

	if (variant.orientation) {
		Object.assign(Cylindo.selectedCylindoFeatures, {ORIENTATION: variant.orientation})
		selectedCylindoFeature.value = `ORIENTATION ${variant.orientation}`
	}

	if (variant.cylindoVariantKeyValue) {
		const splitCylindoVariantKeyValue = variant.cylindoVariantKeyValue.split(' ')
		Object.assign(Cylindo.selectedCylindoFeatures, {[splitCylindoVariantKeyValue[0].toUpperCase()]: splitCylindoVariantKeyValue[1]})
		selectedCylindoFeature.value = selectedCylindoFeature.value + ' ' + variant.cylindoVariantKeyValue
	}

	if (variant.features) {
		const splitCylindoVariantKeyValue = variant.features.split(' ')
		if (splitCylindoVariantKeyValue[0] && splitCylindoVariantKeyValue[1]) {
			Object.assign(Cylindo.selectedCylindoFeatures, {[splitCylindoVariantKeyValue[0].toUpperCase()]: splitCylindoVariantKeyValue[1]})
		} else if (splitCylindoVariantKeyValue[0] && !splitCylindoVariantKeyValue[1]) {
			delete Cylindo.selectedCylindoFeatures[splitCylindoVariantKeyValue[0].toUpperCase()]
		}

		selectedCylindoFeature.value = selectedCylindoFeature.value + ' ' + variant.cylindoVariantKeyValue
	}

	if (Cylindo.shouldLoad() && selectedCylindoFeature.value) {
		Cylindo.setFeatures()
	}
}

const getSelectedMoreProductLabel = (moreOptionGroup) => {
	const selectedMoreOption = moreOptionGroup.moreOptionsDetails.filter(o => o.isSelected)
	if (selectedMoreOption.length) {
		return `${selectedMoreOption[0].name} (+${selectedMoreOption[0].priceAndCampaign.price.formatted})`
	}
	return `${getTranslation('Vælg')} ${moreOptionGroup.name.toLowerCase()}`
}

const hasSpecifications = () => {
	return product?.value.specifications
}

const setSpecifications = (specifications: object) => {
	if (!hasSpecifications()) return

	return Object.entries(specifications)
		.map(([key, value]) => {
			return `
				<dt class="font-bold">${key}</dt>
				<dd>${value}</dd>
			`
		})
		.join('')
}

const productMoreIdModal = ref('')

const hasVariants = () => {
	return product?.value.variantOptionsAdvanced?.variantGroups?.length > 0
}

const isColorGroup = (variantGroup) => {
	return areaSelectedVariantColorGroupIds.value.includes(variantGroup.id)
}

const structureVariantInfoForModal = (variantGroup) => {
	return {
		heading: variantGroup.informationTitle ? variantGroup.informationTitle : variantGroup.name,
		content: variantGroup.informationDescription ? variantGroup.informationDescription : variantGroup.variantDescription
	}
}

const structureMoreProductsInfoForModal = (moreProductsGroup) => {
	return {
		heading: moreProductsGroup.name,
		content: getTranslation('TilvalgInfoboks')
	}
}

// Prepare for variant loading
const isLoading = ref(false)

//Add to cart
let showPowerStep = ref(false)
const addProductToCart = () => {
	const cartObj: AddToCartData = {
		products: [
			...[{
				name: product.value.name,
				productId: product.value.id,
				variantId: selectedVariantId.value,
				quantity: 1,
				preOrder: product.value.priceAndCampaign.preOrder,
				cylindoFeature: selectedCylindoFeature.value
			}]
		]
	}
	console.log('cart', cartObj)
	Object.values(selectedOptionalProducts.value).forEach((optionalProduct) => {
		const splitId = optionalProduct.product.id.split('_')
		cartObj.products.push({
			name: optionalProduct.product.name,
			productId: splitId[0],
			variantId: splitId.length > 1 ? splitId[1] : '',
			quantity: 1,
			cylindoFeature: selectedCylindoFeature.value
		})
	})

	showPowerStep.value = true

	addToCart(cartObj).then(() => {
		showPowerStep.value = true
	})
}

const productError = ref(null)

const getArea = async () => {
	const areaRequest = await fetch(`/dwapi/content/areas/${props.areaId}`)
	const areaResponds = await areaRequest.json()
	const colorGroupItem = areaResponds.Item.Fields.find(itm => itm.SystemName === 'CylindoVariantColorGroups')
	areaSelectedVariantColorGroupIds.value = colorGroupItem.Value.SelectedValues
}

const variantModalData = ref(null)
const loadVariantModal = async (variantGroup) => {
	if(!variantModalData.value){
		variantModalData.value = await getVariantGroupData(variantGroup)
	}

	showVariantModal.value = true
}

const backgroundFabricMap = ref<Record<string, string>>({})

const loadBackgroundFabrics = async (product) => {
	const colorGroups = product?.variantOptionsAdvanced?.variantGroups
	if (!colorGroups) return

	for (const group of colorGroups) {
		if (!group.selectedMaterialType) continue
		const cacheKey = `${group.id}-${group.selectedMaterialType}`
		if (backgroundFabricMap.value[cacheKey]) continue

		const data = await getVariantGroupData(group)
		const result = data?.variantGroupDetails?.variantOptionDetails?.find(
			m => m.materialType === group.selectedMaterialType
		)
		if (result?.designProperties?.backgroundFabric) {
			backgroundFabricMap.value[cacheKey] = result.designProperties.backgroundFabric
		}
	}
}


const variantSelectorData = ref(null)
const moreOptionsSelectorData = ref(null)
const loadVariantSelector = async (variantGroup) => {
	variantSelectorData.value = await getVariantGroupData(variantGroup)
	if (variantSelectorData.value.variantGroupDetails.videoLink === '' && variantGroup.videoLink !== '') {
		variantSelectorData.value.variantGroupDetails.videoLink = variantGroup.videoLink

	}
	showVariantSelector.value = true
}

const loadMoreOptionsSelector = async (moreOptionsGroup, productMoreId = '') => {
	moreOptionsSelectorData.value = moreOptionsGroup
	productMoreIdModal.value = productMoreId
	showMoreOptionsSelector.value = true
}

const getVariantGroupData = async (variantGroup) => {
	const request = await fetch(`/mobler-api/v2/products/variantgroupfeed?variantgroupid=${variantGroup.id}&productid=${product.value.id}&variantid=${product.value.variantId}&cylindoproductid=${product.value.cylindo.id}&languageid=${product.value.languageId}&displaytype=${variantGroup.displayType}`)
	return await request.json()
}

const productOptions = {
	options: {
		priceAndCampaign: true,
		productDetailsAdvanced: true,
		media: true,
		dimensions: true,
		delivery: true,
		cylindo: true,
		specifications: true,
		variantOptionsAdvanced: true,
		moreOptions: true,
		moreOptionsDetails: true,
		usps: true
	}
}

async function getProductData(productIds = '') {
	isLoading.value = true
	if (productIds === '') {
		productIds = `${props.productId}_${props.variantId}`
		if (props.variantId !== 0) {
			productIds = `${props.productId}_${props.variantId}`
		} else {
			productIds = `${props.productId}`
		}
	}

	if (Object.keys(selectedOptionalProducts.value).length) {
		const moreOptionsProducts = []
		Object.values(selectedOptionalProducts.value).forEach((optionalProduct) => {
			moreOptionsProducts.push({
				groupId: optionalProduct.group.id,
				groupName: optionalProduct.group.name,
				selectedProductId: optionalProduct.product.id
			})
		})
		productOptions.moreOptionsProducts = moreOptionsProducts
	} else {
		delete productOptions.moreOptionsProducts
	}


	await fetch(`/mobler-api/v2/products/productsFeed?productIds=${productIds}`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(productOptions)
	})
		.then(response => {
			if (!response.ok) {
				throw new Error('Network response was not ok')
			}
			return response.json()
		})
		.then( async (data) => {
			let products = data.products

			if (products.length > 0) {
				// we only want the first Products object in the Products array of the returned data:
				product.value = products[0]
				await loadBackgroundFabrics(product.value);
				console.log('____backgroundFabricMap', backgroundFabricMap);
				selectedCylindoFeature.value = product.value.cylindo.defaultFeatures
				selectedVariantId.value = product.value.variantId

				trackProduct()

				// Create carouselData
				setCarouselData(product)


				// Set Product USPs
				productBoxes.value = products[0]?.usps?.productBoxes
				productBullets.value = products[0]?.usps?.productBullets

			} else {
				productError.value = 'No product found'
			}
		})

		.catch(error => {
			productError.value = error
			console.error('There has been a problem with your fetch operation:', error)
		})

	isLoading.value = false
}

const trackProduct = () => {
	let ga4EcomItem: GA4EcommerceItem = new GA4EcommerceItem()
	ga4EcomItem.item_id = product.value?.id
	ga4EcomItem.item_name = product.value?.name
	ga4EcomItem.item_brand = product.value?.brand
	ga4EcomItem.item_image = product.value?.media?.images?.find(i => i.isPrimary)?.url ?? product.value?.media?.productImage
	ga4EcomItem.price = product.value?.priceAndCampaign?.price?.raw
	ga4EcomItem.currency = 'DKK'
	ga4EcomItem.item_variant = product.value?.variantId
	ga4EcomItem.item_category = product.value?.categories
	trackToDatalayer({
		event: 'view_item',
		ecommerce: {
			items: [ga4EcomItem]
		}
	})
}

const isLocalWebshopSelected = computed(() => {
	let shopname = settings?.shopname?.toLowerCase()

	if (shopname && shopname != "møblér" && shopname != "mobler" && shopname != "m%c3%b8bl%c3%a9r") {
		return true;
	}
	return false;
})

const toggleShopMenu = (showWithStock = false, instockShops = []) => {
	const customEvent = new CustomEvent('moebler:event:toggleShopMenu', {
		detail: {
			showWithStock: showWithStock,
			instockShops: instockShops.join(',')
		}
	});
	document.dispatchEvent(customEvent);
}

onMounted(async () => {
	await getArea()
	await getProductData()
	const defaultCylindoFeatures = product.value?.cylindo?.defaultFeatures.split('|')
	const otherCylindoFeatures = product.value?.cylindo?.feature.split('|')
	Object.assign(Cylindo.selectedCylindoFeatures, Cylindo.convertCylindoFeatureArrayToObject([...defaultCylindoFeatures, ...otherCylindoFeatures]))
	Cylindo.setFeatures()
	trackProduct()

})

</script>

<template>
	<Spinner v-if="isLoading" class="container min-h-[50vh]">
		{{ getTranslation('Product.Loading') }}
	</Spinner>
	<div v-else-if="productError">
		{{ getTranslation('Product.Error') }} {{ productError }}
	</div>
	<div v-else>
		<div class="container grid xl:grid-cols-4 items-baseline gap-y-2 xl:gap-y-0 xl:gap-x-8 mb-4 xl:mb-2">
			<h1 class="text-balance xl:col-span-3">{{ product?.name }}</h1>
			<p class="color-subtle text-sm xl:text-right">
				{{ translations.ProductNumber }}
				<span>{{ product?.number }}</span>
			</p>
		</div>

		<div class="product grid xl:grid-cols-4 xl:auto-rows-max gap-y-4 gap-x-8 mb-16 container overflow-hidden">

			<ProductCarousel
				v-if="product?.media?.images || product?.media?.videos || product?.cylindo?.isUsingCylindoAssets"
				class="xl:col-span-3"
				:class="carouselData?.cylindo?.isUsingCylindoAssets ? 'js-productcarousel-is-using-cyldino-assets' : ''"
				:carousel-data="carouselData"
				:cylindo-features="carouselData?.cylindo?.isUsingCylindoAssets ? selectedCylindoFeature : ''"
			>
			</ProductCarousel>

			<div
				v-if="hasSpecifications() || product?.productDetailsAdvanced?.providerLink || product?.productDetailsAdvanced?.catalogLink"
				class="shortcuts flex flex-col md:flex-row md:items-center z-0 gap-2 lg:gap-4 xl:col-span-3">
				<div class="hidden md:flex shortcuts__header">
					<small><strong>{{ translations.Shortcuts }}</strong></small>
				</div>
				<ul class="list-none flex flex-wrap gap-4 *:items-center *:gap-2">
					<li v-if="product?.productDetailsAdvanced?.longDescription"
						class="block xl:hidden">
						<RenderAction
							action="button"
							theme="faded"
							size="sm"
							@click="goTo($refs.longDescription)">
							{{ translations.ProductProductDescriptionTitle }}
						</RenderAction>
					</li>
					<li v-if="hasSpecifications()">
						<RenderAction
							action="button"
							theme="faded"
							size="sm"
							@click="goTo($refs.specifications)">
							<font-awesome-icon :icon="['fal', 'clipboard']"/>
							{{ translations.ProductSpecificationsTitle }}
						</RenderAction>
					</li>
					<li v-if="product?.productDetailsAdvanced?.providerLink" class="hidden xl:flex">
						<RenderAction
							:data="product.productDetailsAdvanced.providerLink"
							action="link"
							target="external"
							theme="faded"
							size="sm">
							<font-awesome-icon :icon="['fal', 'pen']"/>
							{{ translations.ProductDesignYourselfTitle }}
						</RenderAction>
					</li>
					<li v-if="product?.productDetailsAdvanced?.catalogLink" class="hidden xl:flex">
						<RenderAction
							:data="product.productDetailsAdvanced.catalogLink"
							action="link"
							target="external"
							theme="faded"
							size="sm">
							<font-awesome-icon :icon="['fal', 'book-open']"/>
							{{ translations.ProductInspirationBrochureTitle }}
						</RenderAction>
					</li>
				</ul>
			</div>

			<!-- Descriptions -->
			<!-- TODO: Make component of descriptions, updates on variant change -->
			<div class="short-description xl:col-span-3">
				<div v-html="product?.shortDescription"></div>
				<ul class="list-none flex gap-x-2 text-base">
					<li v-if="product?.dimensions?.depth > 0">D/L: {{ product?.dimensions.depth }}</li>
					<li v-if="product?.dimensions?.height > 0">H: {{ product?.dimensions.height }}</li>
					<li v-if="product?.dimensions?.width > 0">B: {{ product?.dimensions.width }}</li>
				</ul>
			</div>

			<div v-if="product?.productDetailsAdvanced?.longDescription"
				 class="flex flex-col relative xl:col-span-3"
				 ref="longDescription">
				<h2 class="text-pretty mb-4">{{ translations.ProductProductDescriptionTitle }}</h2>

				<div class="read-more-container js-read-more-container">
					<div class="prose-sm lg:prose lg:max-w-none"
						 v-html="product?.productDetailsAdvanced?.longDescription"></div>
				</div>
				<div class="read-more-fade bg-white js-read-more-fade"
					 style="box-shadow: 0 -2rem 2.5rem 2.5rem rgb(255, 255, 255);">
					<RenderAction
						action="button"
						theme="blackoutline"
						size="sm"
						@click="readMore($refs.longDescription)"
					>
						{{ translations.ProductProductDescriptionReadMoreTitle }}
						<font-awesome-icon :icon="['fas', 'chevron-down']"/>
					</RenderAction>
				</div>
			</div>
			<ul class="list-none flex flex-wrap gap-4 *:items-center *:gap-2">
				<li v-if="product?.productDetailsAdvanced?.providerLink" class="flex xl:hidden">
					<RenderAction
						:data="product.productDetailsAdvanced.providerLink"
						action="link"
						target="external"
						theme="faded"
						size="sm">
						<font-awesome-icon :icon="['fal', 'pen']"/>
						{{ translations.ProductDesignYourselfTitle }}
					</RenderAction>
				</li>
				<li v-if="product?.productDetailsAdvanced?.catalogLink" class="flex xl:hidden">
					<RenderAction
						:data="product.productDetailsAdvanced.catalogLink"
						action="link"
						target="external"
						theme="faded"
						size="sm">
						<font-awesome-icon :icon="['fal', 'book-open']"/>
						{{ translations.ProductInspirationBrochureTitle }}
					</RenderAction>
				</li>
			</ul>

			<!-- Specifications -->
			<!-- TODO: Make component, updates on variant change -->
			<div v-if="hasSpecifications()"
				 class="relative xl:col-span-3"
				 ref="specifications">
				<h2 class="text-pretty mb-4">{{ translations.ProductSpecificationsTitle }}</h2>

				<dl class="read-more-container js-read-more-container flex flex-row flex-wrap *:py-4 *:w-1/2 *:border-t *:border-grey"
					v-html="setSpecifications(product?.specifications)">
				</dl>

				<div class="read-more-fade bg-white js-read-more-fade"
					 style="box-shadow: 0 -2rem 2.5rem 2.5rem rgb(255, 255, 255);">
					<RenderAction
						action="button"
						theme="blackoutline"
						size="sm"
						@click="readMore($refs.specifications)"
					>
						{{ translations.ProductSpecificationsReadMoreTitle }}
						<font-awesome-icon :icon="['fas', 'chevron-down']"/>
					</RenderAction>
				</div>
			</div>

			<div class="row-start-3 xl:row-start-1 xl:row-span-5 xl:col-start-4 ">
				<div class="border-grey border rounded mb-4 flex flex-col">
					<div class="p-4 border-b border-grey">
						<PriceElement
							v-if="product?.priceAndCampaign"
							theme="page"
							:price-data="product?.priceAndCampaign"
						/>
						<ul class="mt-2 list-none"
							v-if="product?.priceAndCampaign?.moreOptionsText">
							<li class="text-xs text-grey-dark leading-2"
								v-if="product?.priceAndCampaign?.moreOptionsText">
								{{ product?.priceAndCampaign?.moreOptionsText }}
							</li>
						</ul>
					</div>

					<!-- Filters -->
					<!-- Filter Group -->
					<div v-if="hasVariants()"
						 class="flex flex-col *:border-b *:border-grey *:p-4">

						<div v-for="variantGroup in product?.variantOptionsAdvanced?.variantGroups">
							<!-- If is a color group -->
							<template v-if="isColorGroup(variantGroup)">
								<!-- If color group should display as modal -->
								<template v-if="variantGroup?.variantOptionDetails?.length === 0">
									<SelectElement
										:label="variantGroup.name"
										:infobox="structureVariantInfoForModal(variantGroup)"
										type="modal"
										@click="loadVariantModal(variantGroup)"
									>

										<span class="flex items-center gap-2">
											<template v-if="variantGroup?.displayType === 'SelectDesign'">
												<span
													v-if="variantGroup?.selectedMaterialType"
													class="inline-block shrink-0 w-6 h-6 rounded-full border border-grey bg-cover bg-center"
													:style="backgroundFabricMap[`${variantGroup.id}-${variantGroup.selectedMaterialType}`]
														? backgroundFabricMap[`${variantGroup.id}-${variantGroup.selectedMaterialType}`]
														: ''"
												/>
												<span
													v-else
													class="inline-block shrink-0 w-6 h-6 rounded-full border border-grey"
													:style="{ backgroundColor: variantGroup?.selectedColorHex }"
												/>
											</template>
											<span class="truncate">{{ variantGroup.selectedName }}</span>
										</span>
									</SelectElement>

									<VariantModal
										v-if="variantModalData"
										:show="showVariantModal"
										:product-data="product"
										:variant-group-data="variantModalData"
										:color-group-ids="areaSelectedVariantColorGroupIds"
										@close="showVariantModal = false"
										@variantChange="variantChange"/>
								</template>

								<!-- If color group should display inline -->
								<template v-else>
									<RadioGroup
										:label="variantGroup.name"
										:infobox="structureVariantInfoForModal(variantGroup)"
										:options="variantGroup.variantOptionDetails"
										@variantChange="variantInlineChange($event, variantGroup)"
									/>
								</template>

							</template>
							<!-- If is other than color group -->
							<template v-else>
								<SelectElement
									:key="variantGroup.$id"
									:options="variantGroup.variantOptionDetails"
									:default="variantGroup.variantOptionDetails.filter(option => option.selected)"
									:label="variantGroup.name"
									type="modal"
									:infobox="structureVariantInfoForModal(variantGroup)"
									@click="loadVariantSelector(variantGroup)">
									{{ variantGroup.selectedName }}
								</SelectElement>
							</template>
						</div>
					</div>

					<div v-if="product?.moreOptions"
						 class="flex flex-col *:border-b *:border-grey *:p-4">
						<div class="flex items-center justify-between gap-4">
							<p class="text-xl">{{ getTranslation('Tilvalg') }}</p>
							<ShowModal
								:modal-title="getTranslation('Tilvalg')"
								:modal-content="getTranslation('TilvalgInfoboks')"
								:button-options="{ action: 'button', theme: 'faded', size: 'sm' }"
							>
								<font-awesome-icon :icon="['fas', 'info-circle']"/>
								{{ getTranslation('Info') }}
							</ShowModal>
						</div>
						<SelectElement
							v-for="moreOption in product?.moreOptions"
							:key="moreOption.$id"
							:options="moreOption.moreOptionsDetails"
							:default="moreOption.moreOptionsDetails.filter(o => o.isSelected)"
							:label="moreOption.name"
							@click="loadMoreOptionsSelector(moreOption)"
							type="modal"
						>
							{{ getSelectedMoreProductLabel(moreOption) }}
						</SelectElement>
					</div>

					<VariantSelector
						v-if="moreOptionsSelectorData && product?.moreOptions"
						type="moreOptions"
						:show="showMoreOptionsSelector"
						:product-data="product"
						:moreOptions-group-data="moreOptionsSelectorData"
						:product-more-id="productMoreIdModal"
						@close="showMoreOptionsSelector = false"
						@addOptionalProduct="addOptionalProduct" @removeOptionalProduct="removeOptionalProduct"
						variant-group-data=""/>

					<VariantSelector
						v-if="variantSelectorData"
						type="variants"
						:show="showVariantSelector"
						:product-data="product"
						:variant-group-data="variantSelectorData"
						@close="showVariantSelector = false"
						@variantChange="variantChange" more-options-group-data=""/>

					<!-- Actions -->
					<div class="flex flex-col gap-4 p-4">
						<RenderAction
							v-if="product?.seeProductInStore"
							:data="product.seeProductInStore"
							action="link"
							theme="link"
							size="lg"
							@click=""
						>
							{{ translations.ProductViewInStore }}
						</RenderAction>

						<!-- If For Sale -->
						<RenderAction
							v-if="(product?.priceAndCampaign?.forSale || product?.priceAndCampaign?.preOrder) && product?.priceAndCampaign?.price?.raw > 0"
							action="button"
							theme="primary"
							size="lg"
							@click="addProductToCart"
						>
							<template v-if="product?.priceAndCampaign?.preOrder">
								{{ translations.PreOrderButton }}
							</template>

							<template v-else>
								<font-awesome-icon :icon="['fas', 'cart-shopping']"/>
								{{ translations.ProductAddToCart }}
							</template>
						</RenderAction>

						<div v-if="!product?.priceAndCampaign?.forSale"
							 class="font-bold w-full text-center opacity-50">
							{{ translations.NotForSaleMessage }}
						</div>

						<div v-if="product?.shopStockInfo?.shopStockProduct">
							<div class="border border-primary rounded p-3 bg-white shadow-md ">

								<div class="flex flex-col gap-2 items-center" v-if="isLocalWebshopSelected">
									<font-awesome-icon :icon="['fas', 'shop']"/>
									<div class="flex flex-col gap-2 items-center"
										 v-if="product?.shopStockInfo?.inStockShopCurrent">
									<span class="flex items-center text-center text-gray-900 font-medium">
										<span class="font-bold text-sm">{{ getTranslation('Oplev i', 'Oplev i') }} {{
												settings?.shopInfos[0]?.ShopName
											}}, {{ getTranslation('Maybe in other size', 'måske i en anden størrelse') }} </span>
									</span>

										<span class="text-sm text-gray cursor-pointer"
											  v-if="settings?.shopInfos[0]?.CurrentOpeningInfoHasMore || !settings?.shopInfos[0]?.CurrentOpeningInfoShow"
											  v-on:click="toggleShopMenu(true)">
										{{ getTranslation('Shop has Multiple', 'Se åbningstider') }}
									</span>
										<span class="text-sm text-gray flex gap-1 cursor-pointer" v-else
											  v-on:click="toggleShopMenu()">
										<span class="text-primary font-bold"
											  v-if="settings?.shopInfos[0]?.CurrentOpeningInfoOpen">{{ getTranslation('Shop is Open', 'Åben') }}</span>
										<span class="text-black font-bold"
											  v-else>{{ getTranslation('Shop is Closed', 'Lukket') }}</span>
										<span>·</span>
										<span>{{ settings?.shopInfos[0]?.CurrentOpeningInfoText }}</span>
									</span>
									</div>
									<div class="flex items-center text-center text-gray-900 font-medium" v-else>
										<span
											class="font-bold text-sm">Ikke på lager i {{ settings?.shopInfos[0]?.ShopName }}</span>
									</div>
								</div>
								<div class="flex items-center justify-between text-gray-900 w-full font-medium"
									 :class="{'border-t border-grey-light pt-2 mt-2': isLocalWebshopSelected}">
								<span class=" text-sm">
									<span
										class="inline-block w-[9px] h-[9px] rounded-full bg-primary me-2"></span> {{ getTranslation('In stock in', 'På lager i') }} {{ product?.shopStockInfo?.inStockShopsCount }} {{ getTranslation('Shops', 'butikker') }}
								</span>
									<RenderAction
										v-on:click="toggleShopMenu(true, product?.shopStockInfo?.inStockShops)"
										action="link"
										theme="none"
										size="sm"
										class="underline"
									>
										{{ getTranslation('Show In stock results', 'Vis') }}
									</RenderAction>
								</div>
							</div>
						</div>
						<div v-else>
							<RenderAction
								v-if="settings?.findDealerLink && !isLocalWebshopSelected"
								v-on:click="toggleShopMenu(false)"
								action="link"
								theme="outline"
								size="lg"
							>
								<font-awesome-icon :icon="['fas', 'shop']"/>
								{{ translations.HeaderNavFindShop }}
							</RenderAction>
							<div
								v-else-if="settings?.findDealerLink && isLocalWebshopSelected && !product?.onlyOnline"
								class="border border-primary rounded p-3 bg-white shadow-md flex flex-col gap-2 items-center"
							>
								<font-awesome-icon :icon="['fas', 'shop']"/>
								<span class="flex items-center text-center text-gray-900 font-medium">

								<span class="font-bold text-sm">{{ getTranslation('Oplev i', 'Oplev i') }} {{
										settings?.shopInfos[0]?.ShopName
									}}, {{ getTranslation('Maybe in other size', 'måske i en anden størrelse') }} </span>
							</span>

								<span class="text-sm text-gray cursor-pointer"
									  v-if="settings?.shopInfos[0]?.CurrentOpeningInfoHasMore || !settings?.shopInfos[0]?.CurrentOpeningInfoShow"
									  v-on:click="toggleShopMenu()">
								{{ getTranslation('Shop has Multiple', 'Se åbningstider') }}
							</span>
								<span class="text-sm text-gray flex gap-1 cursor-pointer" v-else
									  v-on:click="toggleShopMenu(true)">
								<span class="text-primary font-bold"
									  v-if="settings?.shopInfos[0]?.CurrentOpeningInfoOpen">{{ getTranslation('Shop is Open', 'Åben') }}</span>
								<span class="text-black font-bold"
									  v-else>{{ getTranslation('Shop is Closed', 'Lukket') }}</span>
								<span>·</span>
								<span>{{ settings?.shopInfos[0]?.CurrentOpeningInfoText }}</span>
							</span>

							</div>
						</div>

							<any-day-price :price="product?.priceAndCampaign?.price?.raw" />



						<!--						<div v-if="product?.priceAndCampaign?.showFinancing && product?.priceAndCampaign?.pricePerMonthFinancing?.formatted != ''"-->
						<!--							 class="flex justify-center flex-wrap gap-2 rounded p-4 border-grey border">-->
						<!--							<PaymentMethodLogos :financing="true" />-->
						<!--							<div class="flex gap-1">-->
						<!--								<span>{{ product?.priceAndCampaign?.pricePerMonthFinancing?.formatted }}</span>-->
						<!--								<span>{{ translations.ProductFinancingTitle }}</span>-->
						<!--							</div>-->
						<!--						</div>-->
					</div>
				</div>

				<ul class="list-none flex flex-col gap-y-2 *:flex *:items-center *:gap-x-2">
					<li v-if="product?.delivery?.quickDelivery || product?.delivery?.displayAverageDeliveryTime && !product?.delivery?.hideDelivery"
						:style="{ 'color': product.delivery.deliveryColor }">
						<ShowModal
							:modal-title="product?.delivery?.deliveryTime"
							:modal-content="product?.delivery?.deliveryDescription">
							<font-awesome-icon :icon="['fal', 'truck-fast']" class="min-w-[2rem] text-lg"/>
							<small class="text-left">{{ product.delivery.deliveryTime }}</small>
						</ShowModal>
					</li>

					<template v-if="usps">
						<li
							v-for="(usp, index) in productBullets"
							:key="index">
							<ShowModal
								v-if="usp.showContentInModal"
								:modal-title="usp.header"
								:modal-content="usp.contentText">
								<font-awesome-icon :icon="['fal', usp.icon]" class="min-w-[2rem] text-lg"/>
								<small>{{ usp.header }}</small>
							</ShowModal>

							<RenderAction
								v-else-if="usp.link"
								theme="link"
								size="none"
								:link="usp.link">
								<font-awesome-icon v-if="usp.icon" :icon="['fal', usp.icon]"
												   class="min-w-[2rem] text-lg"/>
								<small>{{ usp.header }}</small>
							</RenderAction>

							<template v-else>
								<font-awesome-icon v-if="usp.icon" :icon="['fal', usp.icon]"
												   class="min-w-[2rem] text-lg"/>
								<small>{{ usp.header }}</small>
							</template>
						</li>
					</template>
				</ul>

			</div>
		</div>
	</div>

	<ProductGallery
		v-if="product?.media?.moodImages && product?.media?.moodImages.length > 1"
		:data="product.media"
	></ProductGallery>

	<VideoGallery
		v-if="product?.media?.videos && product?.media?.videos.length > 0 && product?.media?.videos[0].heading"
		:data="product.media.videos">
	</VideoGallery>

	<!-- Video, just for testing the VideoBlock component, not to be used here -->
	<!--	<div v-if="product?.media?.videos"-->
	<!--		class="py-8 container">-->
	<!--		<div v-for="(video, index) in product?.media?.videos" :key="index">-->
	<!--			<div @click="showVideoModal = true">-->
	<!--				Hello-->
	<!--			</div>-->

	<!--			<VideoModal-->
	<!--				:show="showVideoModal"-->
	<!--				:video="video"-->
	<!--				:orientation="video"-->
	<!--				@close="showVideoModal = false"-->
	<!--			>-->
	<!--			</VideoModal>-->
	<!--		</div>-->
	<!--	</div>-->

	<Recommendations
		v-if="relewiseData?.showRecommendations && product?.id"
		type="alsoseen"
		:product-id="product?.id"
		:variant-id="product?.variantId"
		:has-background="true"
		:take="8"
	/>

	<div class="py-16" v-if="productBoxes">
		<div class="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
			<USP v-for="(box, index) in productBoxes" :usp="box" context="product" layout="card" :key="index"></USP>
		</div>
	</div>

	<Recommendations
		v-if="!relewiseData?.userIsAnonymous && relewiseData?.showRecommendations && product?.id"
		type="recently"
		:product-id="product?.id"
		:variant-id="product?.variantId"
		:has-background="true"
		:take="8"
	/>
	<PowerStep
		v-if="product?.id"
		:show="showPowerStep"
		:product="product"
		@close="showPowerStep = false"
	/>
</template>
